/**
 * Work Order List Component
 * Task 59: Work order management for auto repair shops
 */

import { useState, useMemo } from 'react';
import { IndustryWorkOrder } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, Wrench } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface WorkOrderListProps {
  workOrders: IndustryWorkOrder[];
  onEdit?: (workOrder: IndustryWorkOrder) => void;
  onDelete?: (workOrder: IndustryWorkOrder) => void;
  onCreate?: () => void;
}

type StatusFilter = 'all' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
type WorkOrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

const STATUS_LABELS: Record<WorkOrderStatus, string> = {
  pending: 'Aguardando',
  in_progress: 'Em Andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

const STATUS_COLORS: Record<WorkOrderStatus, string> = {
  pending: 'secondary',
  in_progress: 'default',
  completed: 'default',
  cancelled: 'destructive',
};

export function WorkOrderList({ workOrders, onEdit, onDelete, onCreate }: WorkOrderListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Filter work orders
  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter((wo) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesVehicle = wo.metadata?.vehiclePlate?.toString().toLowerCase().includes(term);
        const matchesDiagnosis = wo.metadata?.diagnosis?.toString().toLowerCase().includes(term);
        const matchesWorkPerformed = wo.metadata?.workPerformed?.toString().toLowerCase().includes(term);
        if (!matchesVehicle && !matchesDiagnosis && !matchesWorkPerformed) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && wo.status !== statusFilter) return false;

      return true;
    });
  }, [workOrders, searchTerm, statusFilter]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Ordens de Serviço
              </CardTitle>
              <CardDescription>Gerencie os serviços realizados</CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Nova OS
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por placa, diagnóstico..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="pending">Aguardando</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="completed">Concluídos</SelectItem>
                <SelectItem value="cancelled">Cancelados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredWorkOrders.length} de {workOrders.length} ordens de serviço
          </div>
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredWorkOrders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm || statusFilter !== 'all'
                ? 'Nenhuma ordem de serviço encontrada com os filtros selecionados'
                : 'Nenhuma ordem de serviço cadastrada'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Placa</TableHead>
                    <TableHead>Diagnóstico/Serviço</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Mão de Obra</TableHead>
                    <TableHead className="text-right">Peças</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkOrders.map((workOrder) => {
                    const vehiclePlate = workOrder.metadata?.vehiclePlate as string;
                    const diagnosis = workOrder.metadata?.diagnosis as string;
                    const workPerformed = workOrder.metadata?.workPerformed as string;
                    const laborCost = (workOrder.metadata?.laborCost as number) || 0;
                    const partsCost = (workOrder.metadata?.partsCost as number) || 0;
                    const totalCost = laborCost + partsCost;

                    return (
                      <TableRow key={workOrder.id}>
                        <TableCell>
                          <div className="font-medium font-mono">{vehiclePlate || '-'}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            {diagnosis && (
                              <div className="font-medium">{diagnosis}</div>
                            )}
                            {workPerformed && (
                              <div className="text-sm text-muted-foreground mt-1">
                                {workPerformed}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(workOrder.start_date)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(laborCost)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(partsCost)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(totalCost)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_COLORS[workOrder.status as WorkOrderStatus] as any}>
                            {STATUS_LABELS[workOrder.status as WorkOrderStatus]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(workOrder)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {onDelete && (
                                <DropdownMenuItem
                                  onClick={() => onDelete(workOrder)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
